import { useState, useEffect } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { CheckCircle2, XCircle, Clock, Plus, Settings, Trash2, BarChart3, AlertTriangle } from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { toast } from "sonner";
import { useTranslation } from "../contexts/I18nContext";

const fmt = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);

export default function ApprovalWorkflowPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("requests");

  return (
    <div className="space-y-5" data-testid="approval-page">
      <h1 className="text-2xl font-bold tracking-tight">Workflow Approvazioni</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="requests">Richieste</TabsTrigger>
          <TabsTrigger value="config">Configurazione</TabsTrigger>
          <TabsTrigger value="stats">{t('common.statistics')}</TabsTrigger>
        </TabsList>
        <TabsContent value="requests"><RequestsTab /></TabsContent>
        <TabsContent value="config"><ConfigTab /></TabsContent>
        <TabsContent value="stats"><StatsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function RequestsTab() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ tipo: "pagamento", importo: "", descrizione: "" });
  const [actionNote, setActionNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?stato=${filter}` : "";
      const res = await api.get(`${getApiPrefix()}/approvals/requests${params}`);
      setRequests(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const createRequest = async () => {
    if (!form.importo || !form.descrizione) return toast.error("Compila tutti i campi");
    try {
      await api.post(`${getApiPrefix()}/approvals/requests`, { ...form, importo: Number(form.importo) });
      toast.success("Richiesta creata");
      setShowNew(false);
      setForm({ tipo: "pagamento", importo: "", descrizione: "" });
      load();
    } catch { toast.error("Errore creazione"); }
  };

  const processRequest = async (id, azione) => {
    try {
      await api.put(`${getApiPrefix()}/approvals/requests/${id}`, { azione, note: actionNote });
      toast.success(`Richiesta ${azione}`);
      setActionNote("");
      load();
    } catch { toast.error("Errore"); }
  };

  const statusConfig = {
    in_attesa: { color: "bg-amber-100 text-amber-800", icon: Clock, label: "In attesa" },
    approvato: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Approvato" },
    rifiutato: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Rifiutato" },
  };

  return (
    <div className="space-y-4 pt-4" data-testid="requests-tab">
      <div className="flex items-center justify-between">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="in_attesa">{t('common.pending')}</SelectItem>
            <SelectItem value="approvato">Approvate</SelectItem>
            <SelectItem value="rifiutato">Rifiutate</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild><Button data-testid="new-request-btn"><Plus className="h-4 w-4 mr-1" />Nuova Richiesta</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova Richiesta di Approvazione</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('common.type')}</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pagamento">Pagamento</SelectItem>
                    <SelectItem value="rimborso">Rimborso</SelectItem>
                    <SelectItem value="acquisto">Acquisto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importo (EUR)</Label>
                <Input type="number" value={form.importo} onChange={e => setForm({...form, importo: e.target.value})} placeholder="0.00" data-testid="request-amount" />
              </div>
              <div>
                <Label>{t('common.description')}</Label>
                <Textarea value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} placeholder="Descrivi la richiesta..." data-testid="request-desc" />
              </div>
            </div>
            <DialogFooter><Button onClick={createRequest} data-testid="submit-request-btn">Invia Richiesta</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <PageLoader variant="section" /> : (
        <div className="space-y-3">
          {requests.length > 0 ? requests.map((req, i) => {
            const cfg = statusConfig[req.stato] || statusConfig.in_attesa;
            const Icon = cfg.icon;
            return (
              <Card key={req.id || `req-${i}`} className="hover:shadow-sm transition-all">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${cfg.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-semibold">{req.descrizione}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{req.tipo}</Badge>
                          <span className="text-xs text-muted-foreground">da {req.richiedente_nome}</span>
                          <span className="text-xs text-muted-foreground">{req.created_at ? new Date(req.created_at).toLocaleDateString('it-IT') : ''}</span>
                        </div>
                        {req.approvatore_nome && <p className="text-xs text-muted-foreground mt-1">{cfg.label} da: {req.approvatore_nome}</p>}
                        {req.note_approvazione && <p className="text-xs italic mt-1">"{req.note_approvazione}"</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{fmt(req.importo)}</p>
                      <Badge className={`${cfg.color} mt-1`}>{cfg.label}</Badge>
                    </div>
                  </div>
                  {req.stato === 'in_attesa' && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      <Input placeholder="Note (opzionale)..." value={actionNote} onChange={e => setActionNote(e.target.value)} className="flex-1 text-sm" />
                      <Button size="sm" variant="default" onClick={() => processRequest(req.id, 'approvato')} data-testid={`approve-${i}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" />{t('common.approve')}</Button>
                      <Button size="sm" variant="destructive" onClick={() => processRequest(req.id, 'rifiutato')} data-testid={`reject-${i}`}>
                        <XCircle className="h-4 w-4 mr-1" />{t('common.reject')}</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          }) : (
            <Card><CardContent className="py-12 text-center text-muted-foreground">Nessuna richiesta di approvazione</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}

function ConfigTab() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ soglia_importo: "", livelli_approvazione: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${getApiPrefix()}/approvals/config`);
      setConfigs(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createConfig = async () => {
    if (!form.soglia_importo) return toast.error("Inserisci la soglia");
    try {
      await api.post(`${getApiPrefix()}/approvals/config`, {
        soglia_importo: Number(form.soglia_importo),
        livelli_approvazione: form.livelli_approvazione,
      });
      toast.success("Configurazione creata");
      setShowNew(false);
      load();
    } catch { toast.error("Errore"); }
  };

  const deleteConfig = async (id) => {
    try {
      await api.delete(`${getApiPrefix()}/approvals/config/${id}`);
      toast.success("Eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4 pt-4" data-testid="config-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Configura soglie di importo che richiedono approvazione</p>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild><Button variant="outline"><Plus className="h-4 w-4 mr-1" />Nuova Soglia</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova Soglia Approvazione</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Soglia Importo (EUR)</Label>
                <Input type="number" value={form.soglia_importo} onChange={e => setForm({...form, soglia_importo: e.target.value})} placeholder="5000" />
              </div>
              <div>
                <Label>Livelli di approvazione</Label>
                <Select value={String(form.livelli_approvazione)} onValueChange={v => setForm({...form, livelli_approvazione: Number(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 livello</SelectItem>
                    <SelectItem value="2">2 livelli</SelectItem>
                    <SelectItem value="3">3 livelli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={createConfig}>{t('common.save')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <PageLoader variant="section" /> : configs.length > 0 ? (
        <div className="space-y-2">
          {configs.map((c) => (
            <Card key={c.id || `config-${c.soglia_importo}`}>
              <CardContent className="pt-4 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Pagamenti sopra {fmt(c.soglia_importo)}</p>
                    <p className="text-xs text-muted-foreground">{c.livelli_approvazione} livello/i di approvazione</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={c.attivo ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>{c.attivo ? "Attivo" : "Disattivato"}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => deleteConfig(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nessuna soglia configurata. Aggiungi la prima soglia per attivare il workflow.</CardContent></Card>
      )}
    </div>
  );
}

function StatsTab() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${getApiPrefix()}/approvals/stats`)
      .then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader variant="section" />;

  const statItems = [
    { key: "in_attesa", label: "In Attesa", color: "amber", icon: Clock },
    { key: "approvato", label: "Approvate", color: "emerald", icon: CheckCircle2 },
    { key: "rifiutato", label: "Rifiutate", color: "red", icon: XCircle },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-3 pt-4" data-testid="stats-tab">
      {statItems.map(({ key, label, color, icon: Icon }) => {
        const data = stats?.[key] || { count: 0, importo: 0 };
        return (
          <Card key={key}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className={`h-12 w-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
                  <Icon className={`h-6 w-6 text-${color}-600`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{label}</p>
                  <p className="text-2xl font-bold">{data.count}</p>
                  <p className="text-sm text-muted-foreground">{fmt(data.importo)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
